import { cn } from './cn';

export interface ProgressDotsProps {
  /** Total number of steps. */
  total: number;
  /** Current step index, 0-based. */
  current: number;
  className?: string;
  'aria-label'?: string;
}

/**
 * Visual step indicator for multi-step flows (quiz, onboarding).
 *
 * Quiet Atlas tokens:
 *   - current: terra-600, wider (w-8)
 *   - completed: terra-600 (small)
 *   - future: ink-100 (small)
 */
export function ProgressDots({
  total,
  current,
  className,
  'aria-label': ariaLabel = 'Step progress',
}: ProgressDotsProps) {
  const safeTotal = Math.max(1, Math.floor(total));
  const safeCurrent = Math.min(Math.max(0, Math.floor(current)), safeTotal - 1);

  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={safeTotal}
      aria-valuenow={safeCurrent + 1}
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1.5', className)}
    >
      {Array.from({ length: safeTotal }, (_, i) => {
        const isCurrent = i === safeCurrent;
        const isCompleted = i < safeCurrent;
        return (
          <span
            key={i}
            aria-hidden="true"
            data-state={
              isCurrent ? 'current' : isCompleted ? 'completed' : 'future'
            }
            className={cn(
              'h-2 rounded-full transition-all duration-200',
              isCurrent && 'w-8 bg-terra-600',
              isCompleted && 'w-2 bg-terra-600',
              !isCurrent && !isCompleted && 'w-2 bg-ink-100',
            )}
          />
        );
      })}
    </div>
  );
}
